import { useHistory } from 'react-router-dom';
import { Text, Stack, Button, Link, List } from 'basis';
import React, { useCallback, useState } from 'react';
import { LayoutPage } from 'components';
import { Routes } from 'types';
import { config } from '_config';
import styled from '@emotion/styled';
import { LimitedWidth } from 'components/layout-page/LimitedWidth';

const ButtonContainer = styled.div`
  width: 100%;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: 250px;
    margin: 0;
  }
`;

const Bold = styled.span`
  font-weight: 600;
`;

export const NzAmlConsents: React.FC = () => {
  const [isAgreeToTerms, setIsAgreeToTerms] = useState(false);
  const [showError, setShowError] = useState(false);
  const history = useHistory();

  const handleContinue = useCallback(() => {
    if (isAgreeToTerms) {
      setShowError(false);
      history.push(Routes.achGreenIdVerification);
    } else {
      setShowError(true);
      document.getElementById('agreeToTerms')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [history, isAgreeToTerms]);

  const handleCheckboxChange = useCallback(() => {
    setIsAgreeToTerms(!isAgreeToTerms);
    if (showError) {
      setShowError(false);
    }
  }, [isAgreeToTerms, showError]);

  return (
    <LayoutPage step="update" hideProgressStepper>
      <LimitedWidth>
        <Stack gap="8">
          <Text as="h1" textStyle="heading5" textStyle-md="heading4">
            {config.routeConfig[Routes.nzAmlConsents].pageTitle}
          </Text>

          <NzConsent
            isAgreeToTerms={isAgreeToTerms}
            showError={showError}
            handleCheckboxChange={handleCheckboxChange}
          />
          <ButtonContainer>
            <Button onClick={handleContinue} width="100%">
              Continue
            </Button>
          </ButtonContainer>
        </Stack>
      </LimitedWidth>
    </LayoutPage>
  );
};

type NzConsentProps = {
  isAgreeToTerms: boolean;
  showError: boolean;
  handleCheckboxChange: () => void;
};
function NzConsent({ isAgreeToTerms, showError, handleCheckboxChange }: NzConsentProps) {
  return (
    <>
      {showError && (
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="agreeToTerms" style={{ cursor: 'pointer' }}>
            <Text color="conditional.negative.text">
              <strong>
                Please check the agree indicator to show that you have read and agreed to the Product Disclosure
                Statement.
              </strong>
            </Text>
          </label>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
        <input
          id="agreeToTerms"
          type="checkbox"
          name="agreeToTerms"
          style={{
            width: '20px',
            height: '20px',
            marginTop: '3px',
            cursor: 'pointer',
          }}
          checked={isAgreeToTerms}
          onChange={handleCheckboxChange}
        />
        <div style={{ flex: 1 }}>
          <Stack gap="4">
            <label htmlFor="agreeToTerms" style={{ cursor: 'pointer' }}>
              <Text>
                I apply to Latitude Financial Services Limited (‘Latitude’) for a Gem Visa credit card. I am a New
                Zealand resident and at least 18 years of age.
              </Text>
            </label>

            <Text><Bold>I agree that:</Bold></Text>

            <List type="ordered">
              <List.Item>
                <Text>
                  Each use of the credit card will be subject to the{' '}
                  <Link
                    href="https://assets.gemfinance.co.nz/legals/conditions-of-use/gemvisa-nz/cou.pdf?_ga=2.236588865.1323252382.1615416779-941858851.1587360214"
                    newTab
                  >
                    Gem Visa Conditions of Use
                  </Link>{' '}
                  and the{' '}
                  <Link href="https://assets.latitudefinancial.com/gem-essentials/nz-gem-visa-ids.pdf" newTab>
                    Initial Disclosure Statement
                  </Link>{' '}
                  for the applied account;
                </Text>
              </List.Item>
              <List.Item>
                <Text>
                  I will comply with the terms of the Gem Visa Conditions of Use and the Initial Disclosure Statement.
                </Text>
                <div style={{ height: '14px' }} />
                <Text>
                  I acknowledge that: (i) the information that I provided in this application is true and correct and
                  that no information which is or could be relevant to Latitude accepting my application has been
                  withheld; and (ii) I have read and understood the terms of the Gem Visa Conditions of Use and the
                  Initial Disclosure Statement.
                </Text>
              </List.Item>
              <List.Item>
                <Text>
                  I understand that Latitude collects and uses the information provided about me to facilitate
                  processing my application. Further information on how Latitude collects, uses, discloses and stores
                  personal information, including how I can access and seek correction of my personal information or
                  complain about Latitude's handling of my personal information, is available in Latitude’s{' '}
                  <Link href="https://www.gemfinance.co.nz/privacy/" newTab>
                    Privacy and Credit Reporting Policy
                  </Link>
                  .
                </Text>
              </List.Item>
            </List>
          </Stack>
        </div>
      </div>

      <Stack gap="4">
        <div style={{ marginTop: '32px' }}>
          <Text textStyle="heading5">
            <strong>Important Information Notice</strong>
          </Text>
        </div>
        <Text>
          You will be directed to verification options on the next screen. These may include checks such as a Driver
          Licence verification or a review of your credit report for identification-verification purposes only.
          Please note that undertaking these checks will not affect your credit rating. You may choose not to
          provide consent for these verification checks.
        </Text>

        <Text>
          The information you provide in your application is collected and stored by Latitude. Some of this
          information - such as your name, date of birth, address and reference numbers including your Driver
          Licence number (together referred to as ‘your personal information’) - is disclosed to Green ID. Green ID
          is owned and operated by GBG ANZ Pty Ltd, which provides electronic identity-verification services to
          Latitude.
        </Text>

        <Text>
          To enable Latitude to verify your identity and meet its legal obligations under Anti-Money Laundering
          laws, Latitude discloses your personal information to Green ID. Green ID then collects, uses and discloses
          your personal information to the data sources that you select when using the Green ID service.
        </Text>

        <Text>
          Latitude does not collect, use, or store any of the reference numbers that you provide directly to the
          data sources you select as part of the Green ID electronic identity-verification process.
        </Text>
      </Stack>
    </>
  );
}
